<template>
  <WizardModal
    :show-modal="isVisible"
    :modal-title="t('Criar link de pagamento')"
    :steps="wizardSteps"
    :step-data="formData"
    :loading="isLoading"
    size="large"
    orientation="vertical"
    :show-step-description="true"
    :reset-on-close="true"
    :allow-step-click="true"
    @cancel="handleClose"
    @close="handleClose"
    @finish="handleFinish"
  >
    <!-- Step 0: Amount -->
    <template #step-0>
      <PaymentLinkStepAmount
        ref="stepAmountRef"
        v-model="formData"
      />
    </template>

    <!-- Step 1: Payment Methods -->
    <template #step-1>
      <PaymentLinkStepMethods
        ref="stepMethodsRef"
        v-model="formData"
      />
    </template>

    <!-- Step 2: Installments -->
    <template #step-2>
      <PaymentLinkStepInstallments
        ref="stepInstallmentsRef"
        v-model="formData"
      />
    </template>

    <!-- Step 3: Options -->
    <template #step-3>
      <PaymentLinkStepOptions
        ref="stepOptionsRef"
        v-model="formData"
      />
    </template>

    <!-- Step 4: Confirmation -->
    <template #step-4>
      <PaymentLinkStepConfirm
        ref="stepConfirmRef"
        :value="formData"
      />
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { usePaymentLinks } from '../../composables/usePaymentLinks';
import { useDateFormatter } from '../../composables/useDateFormatter';

// Step components
import PaymentLinkStepAmount from './wizard/PaymentLinkStepAmount.vue';
import PaymentLinkStepMethods from './wizard/PaymentLinkStepMethods.vue';
import PaymentLinkStepInstallments from './wizard/PaymentLinkStepInstallments.vue';
import PaymentLinkStepOptions from './wizard/PaymentLinkStepOptions.vue';
import PaymentLinkStepConfirm from './wizard/PaymentLinkStepConfirm.vue';

interface PaymentLinkFormData {
  amount: number;
  payment_methods: string[];
  charge_type: 'single' | 'installment';
  installments: number;
  due_date: string | null;
  description: string;
}

interface StepInstance {
  isValid?: { value: boolean };
}

interface CreatedPaymentLink {
  id?: number | string;
  [key: string]: unknown;
}

interface WizardOpenPayload {
  owner?: string;
  ownerId?: number | string;
  defaultAmount?: number;
  customerPhone?: string;
  customerName?: string;
}

const { t } = useI18n();
const { create } = usePaymentLinks();
const { formatDate } = useDateFormatter();

// State
const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const owner = ref<string>('');
const ownerId = ref<number | string | null>(null);
const defaultAmount = ref<number>(0);
const customerPhone = ref<string>('');
const customerName = ref<string>('');

// Step refs for validation
const stepAmountRef = ref<StepInstance | null>(null);
const stepMethodsRef = ref<StepInstance | null>(null);
const stepInstallmentsRef = ref<StepInstance | null>(null);
const stepOptionsRef = ref<StepInstance | null>(null);
const stepConfirmRef = ref<StepInstance | null>(null);

// Form data
const formData = ref<PaymentLinkFormData>({
  amount: 0,
  payment_methods: ['pix', 'boleto', 'credit_card'],
  charge_type: 'single',
  installments: 1,
  due_date: null,
  description: ''
});

// Wizard step configuration
const wizardSteps = computed(() => [
  {
    title: t('Valor'),
    description: t('Defina o valor do pagamento'),
    icon: 'pi pi-dollar',
    validate: () => stepAmountRef.value?.isValid?.value ?? formData.value.amount > 0
  },
  {
    title: t('Formas de Pagamento'),
    description: t('Escolha as formas aceitas'),
    icon: 'pi pi-credit-card',
    validate: () => stepMethodsRef.value?.isValid?.value ?? (formData.value.payment_methods?.length > 0)
  },
  {
    title: t('Parcelamento'),
    description: t('Configure o parcelamento'),
    icon: 'pi pi-wallet',
    validate: () => stepInstallmentsRef.value?.isValid?.value ?? true
  },
  {
    title: t('Opções'),
    description: t('Vencimento e descrição'),
    icon: 'pi pi-cog',
    validate: () => stepOptionsRef.value?.isValid?.value ?? true
  },
  {
    title: t('Confirmação'),
    description: t('Revise e confirme'),
    icon: 'pi pi-check-circle',
    validate: () => true
  }
]);

// Methods
const generateDefaultDescription = (): string => {
  const parts: string[] = [];
  if (customerName.value) {
    parts.push(customerName.value);
  }
  const today = new Date();
  parts.push(formatDate(today));
  return parts.join(' - ');
};

const resetForm = (): void => {
  formData.value = {
    amount: defaultAmount.value,
    payment_methods: ['pix', 'boleto', 'credit_card'],
    charge_type: 'single',
    installments: 1,
    due_date: null,
    description: generateDefaultDescription()
  };
};

const handleFinish = async (): Promise<void> => {
  log('PaymentLinksCreateWizard', 'handleFinish', 'Creating payment link', formData.value);
  isLoading.value = true;

  try {
    const dados = await create({
      owner: owner.value,
      owner_id: Number(ownerId.value ?? 0),
      ...formData.value
    });
    if (!dados) {
      throw new Error('No data returned from payment link creation');
    }
    // TODO: tighten contract (SubaccountsLinks_CriarResponseDados is unknown[]) once PHP contract is updated
    const result = dados as unknown as CreatedPaymentLink;

    log('PaymentLinksCreateWizard', 'handleFinish', 'Created', result);
    eventBus.emit('globalMessage.success', t('Link de pagamento criado com sucesso!'));
    eventBus.emit('payment-link.created', {
      owner: owner.value,
      ownerId: ownerId.value,
      paymentLink: result
    });

    // Close wizard and open view modal with the created link
    handleClose();
    eventBus.emit('modal.payment-link.open', {
      id: result.id,
      customerPhone: customerPhone.value,
      customerName: customerName.value
    });
  } catch (error) {
    log('PaymentLinksCreateWizard', 'handleFinish', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao criar link de pagamento'));
  } finally {
    isLoading.value = false;
  }
};

const handleClose = (): void => {
  log('PaymentLinksCreateWizard', 'handleClose', 'Closing wizard');
  isVisible.value = false;
  resetForm();
};

const handleOpen = (data?: WizardOpenPayload): void => {
  log('PaymentLinksCreateWizard', 'handleOpen', 'Opening wizard', data);

  // Store context
  owner.value = data?.owner || '';
  ownerId.value = data?.ownerId ?? null;
  defaultAmount.value = data?.defaultAmount || 0;
  customerPhone.value = data?.customerPhone || '';
  customerName.value = data?.customerName || '';

  // Reset form with defaults
  resetForm();

  isVisible.value = true;
};

// Lifecycle
onMounted(() => {
  log('PaymentLinksCreateWizard', 'onMounted', 'Component mounted');

  // Listen for create wizard events
  eventBus.on('modal.payment-link-wizard.open', handleOpen);
  eventBus.on('modal.payment-link-wizard.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('modal.payment-link-wizard.open', handleOpen);
  eventBus.off('modal.payment-link-wizard.close', handleClose);
});

// Expose methods
defineExpose({
  open: handleOpen,
  close: handleClose
});
</script>
